// src/pages/SharePage.tsx
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle,
  Copy,
  Twitter,
  Facebook,
  MessageCircle,
  Mail,
  Heart,
  Loader2,
  Send,
  ArrowRight,
} from "lucide-react";
import { useCard } from "../hooks/useCard";
import { useAuth } from "../hooks/useAuth";
import { ENV } from "../config/environment";
import toast from "react-hot-toast";
import { tiktokPixelService } from "../services/tiktok-pixel.service";

// Archetype background configurations - Brighter with gold instead of rose
const ARCHETYPE_BG = {
  moments: {
    gradient:
      "radial-gradient(circle at 30% 40%, #f9ca2466 0%, transparent 60%), radial-gradient(circle at 70% 60%, #f6e58d66 0%, transparent 60%)",
    particles: "#f9ca24",
    accent: "from-yellow-400/30 via-transparent to-transparent",
    overlay: "rgba(249, 202, 36, 0.15)",
  },
  heartbeat: {
    gradient:
      "radial-gradient(circle at 20% 30%, #ffd70066 0%, transparent 60%), radial-gradient(circle at 80% 70%, #ffb34766 0%, transparent 60%)",
    particles: "#ffd700",
    accent: "from-yellow-500/30 via-transparent to-transparent",
    overlay: "rgba(255, 215, 0, 0.15)",
  },
  safehaven: {
    gradient:
      "radial-gradient(circle at 40% 50%, #a5b1c266 0%, transparent 60%), radial-gradient(circle at 60% 40%, #778ca366 0%, transparent 60%)",
    particles: "#a5b1c2",
    accent: "from-blue-400/30 via-transparent to-transparent",
    overlay: "rgba(165, 177, 194, 0.15)",
  },
};

export default function SharePage() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { card, loading, error } = useCard(cardId);
  const [copied, setCopied] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!isAuthenticated && !loading && !hasRedirected.current) {
      hasRedirected.current = true;
      toast.error("Please sign in");
      navigate("/signin");
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (!loading && error) {
      if (retryCount < 1) {
        const timer = setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          window.location.reload();
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
    if (!loading && !card && !error && !hasRedirected.current) {
      hasRedirected.current = true;
      toast.error("Experience not found");
      setTimeout(() => navigate("/dashboard"), 3000);
    }
  }, [loading, card, error, navigate, retryCount]);

  useEffect(() => {
    if (!card) return;

    tiktokPixelService.trackViewContent(
      {
        content_id: card.id,
        content_type: "product",
        content_name: `${card.archetype || "heartbeat"} Share Page`,
      },
      Number(card.bouquetAmount || 0),
      "KES",
      `viewcontent:share:${card.id}`
    );
  }, [card]);

  if (loading || (!card && !error)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="mb-4"
          >
            <Loader2 size={40} className="text-white/20 mx-auto" />
          </motion.div>
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 italic">
            Finalizing your moment...
          </p>
        </div>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Heart size={40} className="text-white/10 mx-auto mb-6" />
          <h2 className="text-2xl font-serif italic text-white mb-4">This moment doesn't exist</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white transition-colors"
          >
            Return
          </button>
        </div>
      </div>
    );
  }

  const archetypeId = card.archetype || "heartbeat";
  const bgConfig = ARCHETYPE_BG[archetypeId as keyof typeof ARCHETYPE_BG] || ARCHETYPE_BG.heartbeat;
  const shareableLink = `${ENV.APP_URL}/recipient/${card.shareableLink}`;
  const primary = card.primaryColor || "#C5A059";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);

      tiktokPixelService.track(
        "AddToWishlist",
        {
          contents: [
            {
              content_id: card.id,
              content_type: "product",
              content_name: "Share Link Copied",
            },
          ],
          value: Number(card.bouquetAmount || 0),
          currency: "KES",
        },
        `wishlist:sharecopy:${card.id}`
      );

      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const shareVia = (platform: string) => {
    const text = encodeURIComponent(`I've prepared something special for you on LRNSY.`);
    const url = encodeURIComponent(shareableLink);
    let shareUrl = "";
    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${text}%20${url}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=A%20Private%20Moment%20for%20You&body=${text}%20${url}`;
        break;
    }
    if (shareUrl) window.open(shareUrl, "_blank");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black text-white overflow-hidden"
    >
      {/* Brighter Themed Ambient Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Archetype-specific gradients - Brighter */}
        <div
          className="absolute inset-0"
          style={{
            background: bgConfig.gradient,
          }}
        />

        {/* Primary color glow - Brighter */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${primary}40 0%, transparent 70%)`,
          }}
        />

        {/* Floating particles - More visible */}
        <div className="absolute inset-0">
          {[...Array(40)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 4 + 1 + "px",
                height: Math.random() * 4 + 1 + "px",
                background: i % 2 === 0 ? bgConfig.particles : "#fff",
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                filter: "blur(1px)",
                opacity: 0.4,
              }}
              animate={{
                y: [0, -40, 0],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            />
          ))}
        </div>

        {/* Archetype-specific overlay - Brighter */}
        <div
          className="absolute inset-0 mix-blend-overlay"
          style={{
            background: `radial-gradient(circle at 30% 40%, ${bgConfig.overlay} 0%, transparent 70%)`,
          }}
        />
      </div>

      {/* Main Container - Full height with proper scrolling */}
      <div className="relative h-full w-full overflow-y-auto z-10 mt-8">
        <div className="min-h-full flex items-center justify-center p-3 sm:p-4 md:p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-5xl"
          >
            <div className="relative bg-[#141414]/90 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
              {/* Header - Compact */}
              <div className="p-4 sm:p-6 md:p-8 pb-2 sm:pb-3 md:pb-4 text-center border-b border-white/5">
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full mb-2 sm:mb-3 md:mb-4"
                  style={{
                    background: `radial-gradient(circle at center, ${primary}30, transparent)`,
                    border: `1px solid ${primary}40`,
                  }}
                >
                  <Send size={16} className="sm:hidden" style={{ color: primary }} />
                  <Send
                    size={18}
                    className="hidden sm:block md:hidden"
                    style={{ color: primary }}
                  />
                  <Send size={22} className="hidden md:block" style={{ color: primary }} />
                </motion.div>
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-serif italic text-white/90 mb-0.5 sm:mb-1">
                  Your moment is ready
                </h1>
                <p className="text-[7px] sm:text-[8px] md:text-[9px] uppercase tracking-[0.4em] text-white/30">
                  Share the private link
                </p>
              </div>

              {/* Two Column Layout - Responsive */}
              <div className="flex flex-col md:grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-5 lg:gap-6 p-3 sm:p-4 md:p-5 lg:p-6">
                {/* Left Column - Share Options */}
                <div className="space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-6">
                  {/* Link Display */}
                  <div className="space-y-1 sm:space-y-1.5">
                    <label className="text-[6px] sm:text-[7px] md:text-[8px] uppercase tracking-[0.3em] font-medium text-white/40 block">
                      Private Link
                    </label>
                    <div className="relative group">
                      <div className="w-full p-2 sm:p-2.5 md:p-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-[9px] sm:text-xs font-mono text-white/60 truncate pr-10 sm:pr-12">
                        {shareableLink}
                      </div>
                      <button
                        onClick={copyToClipboard}
                        className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-all"
                      >
                        {copied ? (
                          <CheckCircle size={12} className="text-green-400" />
                        ) : (
                          <Copy size={12} className="text-white/40" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Share Grid - Compact */}
                  <div className="space-y-1 sm:space-y-1.5">
                    <label className="text-[6px] sm:text-[7px] md:text-[8px] uppercase tracking-[0.3em] font-medium text-white/40 block">
                      Share via
                    </label>
                    <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                      {[
                        { id: "whatsapp", icon: MessageCircle, label: "WA" },
                        { id: "twitter", icon: Twitter, label: "X" },
                        { id: "facebook", icon: Facebook, label: "FB" },
                        { id: "email", icon: Mail, label: "Mail" },
                      ].map((p) => (
                        <button
                          key={p.id}
                          onClick={() => shareVia(p.id)}
                          className="flex flex-col items-center gap-0.5 sm:gap-1 p-2 sm:p-2.5 md:p-3 bg-white/[0.02] border border-white/5 rounded-lg sm:rounded-xl hover:bg-white/10 hover:border-white/20 transition-all group"
                        >
                          <p.icon size={12} className="text-white/40 group-hover:text-white" />
                          <span className="text-[5px] sm:text-[6px] md:text-[7px] uppercase tracking-widest text-white/20">
                            {p.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-3 pt-1 sm:pt-2">
                    <div className="bg-white/5 rounded-lg sm:rounded-xl p-2 sm:p-2.5 md:p-3 border border-white/5">
                      <p className="text-[6px] sm:text-[7px] uppercase tracking-wider text-white/30 mb-0.5">
                        For
                      </p>
                      <p className="text-xs sm:text-sm font-serif truncate text-white">
                        {card.recipientName}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg sm:rounded-xl p-2 sm:p-2.5 md:p-3 border border-white/5">
                      <p className="text-[6px] sm:text-[7px] uppercase tracking-wider text-white/30 mb-0.5">
                        From
                      </p>
                      <p className="text-xs sm:text-sm font-serif truncate text-white">
                        {card.senderName}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Preview Card */}
                <div className="mt-2 md:mt-0">
                  <div className="relative">
                    <div className="relative aspect-[4/3] rounded-lg sm:rounded-xl shadow-2xl overflow-hidden">
                      {/* Background Image - User uploaded image */}
                      {card.recipientImage ? (
                        <img
                          src={card.recipientImage}
                          alt="Memory"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#fdfaf5] to-[#f0e9e0]" />
                      )}

                      {/* Dark overlay for text visibility - More subtle */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />

                      {/* Archetype Glow */}
                      <div
                        className="absolute top-0 right-0 w-16 sm:w-20 md:w-24 lg:w-32 h-16 sm:h-20 md:h-24 lg:h-32 opacity-30 rounded-full blur-3xl"
                        style={{ background: primary }}
                      />

                      {/* Content with improved text visibility */}
                      <div
                        className="relative z-10 h-full cursor-pointer flex flex-col justify-between p-3 sm:p-4 md:p-5 text-white"
                        onClick={() => navigate(`/recipient/${card.shareableLink}`)}
                      >
                        {/* Top section */}
                        <div>
                          <div
                            className="h-px w-4 sm:w-5 md:w-6 mb-1.5 sm:mb-2 md:mb-3"
                            style={{ backgroundColor: `${primary}cc` }}
                          />
                          <p className="text-[5px] sm:text-[6px] md:text-[7px] uppercase tracking-widest text-white/80 mb-0.5 font-medium">
                            To
                          </p>
                          <p className="font-serif italic text-xs sm:text-sm md:text-base lg:text-lg text-white drop-shadow-lg">
                            {card.recipientName}
                          </p>
                        </div>

                        {/* Center section */}
                        <div className="flex flex-col items-center">
                          <div
                            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 rounded-full mb-1 sm:mb-1.5 md:mb-2 flex items-center justify-center backdrop-blur-sm"
                            style={{
                              backgroundColor: `${primary}30`,
                              border: `1px solid ${primary}60`,
                            }}
                          >
                            <Heart size={10} className="sm:hidden" style={{ color: primary }} />
                            <Heart
                              size={12}
                              className="hidden sm:block md:hidden"
                              style={{ color: primary }}
                            />
                            <Heart
                              size={14}
                              className="hidden md:block lg:hidden"
                              style={{ color: primary }}
                            />
                            <Heart
                              size={16}
                              className="hidden lg:block"
                              style={{ color: primary }}
                            />
                          </div>
                          <p className="text-[4px] sm:text-[5px] md:text-[6px] uppercase tracking-[0.3em] text-white/80 text-center font-medium drop-shadow">
                            A special moment
                          </p>
                        </div>

                        {/* Bottom section */}
                        <div className="text-right">
                          <p className="text-[5px] sm:text-[6px] md:text-[7px] uppercase tracking-widest text-white/80 mb-0.5 font-medium">
                            From
                          </p>
                          <p className="font-serif italic text-xs sm:text-sm md:text-base lg:text-lg text-white drop-shadow-lg">
                            {card.senderName}
                          </p>
                          <div
                            className="h-px w-4 sm:w-5 md:w-6 mt-1 sm:mt-1.5 md:mt-2 ml-auto"
                            style={{ backgroundColor: `${primary}cc` }}
                          />
                        </div>
                      </div>

                      {/* Gold accent border */}
                      <div
                        className="absolute inset-0 pointer-events-none rounded-lg sm:rounded-xl"
                        style={{
                          boxShadow: `inset 0 0 0 1px ${primary}40`,
                        }}
                      />
                    </div>
                    <p className="text-center text-[5px] sm:text-[6px] md:text-[7px] uppercase tracking-[0.4em] text-white/30 mt-1 sm:mt-1.5 md:mt-2">
                      Preview
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer - Compact */}
              <div className="px-3 sm:px-4 md:px-5 lg:px-6 py-2 sm:py-3 md:py-4 border-t border-white/5 flex items-center justify-between">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="text-[6px] sm:text-[7px] md:text-[8px] uppercase tracking-[0.3em] text-white/30 hover:text-white/60 transition-colors"
                >
                  Dashboard
                </button>

                <button
                  onClick={() => navigate("/archetypes")}
                  className="group flex items-center gap-1.5 sm:gap-2 md:gap-3 bg-white/10 hover:bg-white/20 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full transition-all"
                >
                  <span className="text-[6px] sm:text-[7px] md:text-[8px] font-medium uppercase tracking-widest text-white/80">
                    Create New
                  </span>
                  <ArrowRight
                    size={8}
                    className="text-white/60 group-hover:translate-x-0.5 transition-transform sm:hidden"
                  />
                  <ArrowRight
                    size={10}
                    className="text-white/60 group-hover:translate-x-0.5 transition-transform hidden sm:block md:hidden"
                  />
                  <ArrowRight
                    size={12}
                    className="text-white/60 group-hover:translate-x-0.5 transition-transform hidden md:block"
                  />
                </button>
              </div>
            </div>

            {/* Brand Signature */}
            <div className="mt-2 sm:mt-3 md:mt-4 text-center">
              <p className="text-[6px] sm:text-[7px] md:text-[8px] uppercase tracking-[0.8em] text-white/20">
                LRNSY
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
